/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { useCallback } from "react";
import LZString from "lz-string";
import { IFilterValue } from "Interfaces/IFilterValue";
import * as ApplicationConstants from "Constants/ApplicationConstants";

/**
 * Hook to compress and decompress the data using LZString.
 * @returns encode and decode functions to compress and decompress the data
 */
export const useLZString = () => {
  const encode = useCallback((data: any): string => {
    return LZString.compressToEncodedURIComponent(JSON.stringify(data));
  }, []);

  const decode = useCallback((encodedData: string): any => {
    const decompressedText = LZString.decompressFromEncodedURIComponent(encodedData);
    return decompressedText ? JSON.parse(decompressedText) : null;
  }, []);

  return { encode, decode };
};

/**
 * Check the length of the query parameter
 * @param queryParam string to validate
 * @returns boolean value
 */
export const validUrlLength = (queryParam: string): boolean => {
  const isValid =
    queryParam && queryParam.length <= ApplicationConstants.maxURLLength ? true : false;
  return isValid;
};

/**
 * Remove the query parameter from the MVP application URL.
 * Replace the URL with the new URL without the query parameter.
 * @param param to remove from the URL
 */
export const removeQueryParams = (param: string) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  urlSearchParams.delete(param);
  window.history.replaceState({}, "", window.location.pathname);
};

/**
 * Helper function to add filter values to the array
 * @param urlFilterValues the array to add filter values to
 * @param fieldName the field name of the filter
 * @param values the values to add
 */
const addFilterValues = (urlFilterValues: IFilterValue[], fieldName: string, values: any[]) => {
  values.forEach((value: any) => {
    urlFilterValues.push({ fieldName, value });
  });
};

/**
 * Only for MVPs
 * Create the filter values from the query parameters.
 * @param request the query parameters
 * @returns the filter values
 */
export const getFiltersFromQueryParams = (request: any): IFilterValue[] => {
  const urlFilterValues: IFilterValue[] = [];

  // Application Type
  if (request.applicationType) {
    urlFilterValues.push({
      fieldName: ApplicationConstants.APPLICATION_TYPE,
      value: request.applicationType,
    });
  }

  // Application Status
  if (request.applicationStatusList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.APPLICATION_STATUS,
      request.applicationStatusList
    );
  }

  // Review Status
  if (request.reviewStatusList?.length) {
    addFilterValues(urlFilterValues, ApplicationConstants.REVIEW_STATUS, request.reviewStatusList);
  }

  // Cohort
  if (request.cohort) {
    urlFilterValues.push({
      fieldName: ApplicationConstants.COHORT,
      value: request.cohort,
    });
  } else {
    urlFilterValues.push({
      fieldName: ApplicationConstants.COHORT,
      value: ApplicationConstants.ANY,
    });
  }

  // Assigned To
  if (request.assignedToMe) {
    urlFilterValues.push({
      fieldName: ApplicationConstants.ASSIGNED_TO,
      value: request.assignedToMe ? ApplicationConstants.ME : ApplicationConstants.ANY,
    });
  }

  // Award Category
  if (request.technologyAreaGroupList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.TECHNOLOGY_AREA_GROUP,
      request.technologyAreaGroupList
    );
  }

  // Technology Area
  if (request.technologyAreaList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.TECHNOLOGY_AREA,
      request.technologyAreaList
    );
  }

  // TA Status
  if (request.technologyAreaStatusList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.TECHNOLOGY_AREA_STATUS,
      request.technologyAreaStatusList
    );
  }

  // TA Score
  if (request.technologyAreaScoreList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.TECHNOLOGY_AREA_SCORE,
      request.technologyAreaScoreList
    );
  }

  // TA Reason
  if (request.technologyAreaStatusReasonList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.TECHNOLOGY_AREA_STATUS_REASON,
      request.technologyAreaStatusReasonList
    );
  }

  // Reason for Flag
  if (request.flagReason?.length) {
    addFilterValues(urlFilterValues, ApplicationConstants.FLAG_REASON, request.flagReason);
  }

  // CPM List
  if (request.availableCpmList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.AVAILABLE_CPM_LIST,
      request.availableCpmList
    );
  }

  // Country
  if (request.countryRegionList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.APPLICATION_COUNTRY_REGION,
      request.countryRegionList
    );
  }

  // State/Province
  if (request.stateProvinceList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.APPLICATION_STATE_PROVINCE,
      request.stateProvinceList
    );
  }

  // Grant Annual Exception
  if (request.annualExceptionList?.length) {
    addFilterValues(
      urlFilterValues,
      ApplicationConstants.GRANT_ANNUAL_EXCEPTION,
      request.annualExceptionList
    );
  }

  return urlFilterValues;
};

/**
 * Only for MVPs
 * Create the request from selected filters to append in query parameters.
 * @param searchValue the search value, empty by default
 * @param selectedApplicationsFilterValues the selected filter values
 * @param page the page number by default 1
 * @param pageSize the page size by default 16
 * @param adminEmail the admin email
 * @param applicationType the application type
 * @param sortColumn the sort column by default empty
 * @param sortDescending  the sort descending by default false
 * @returns
 */
export const setFilterToQueryParams = (
  searchValue,
  selectedApplicationsFilterValues,
  page,
  pageSize,
  adminEmail,
  applicationType,
  sortColumn,
  sortDescending
): {} => {
  const requestBody = {
    applicationStatusList: [],
    countryRegionList: [],
    pageIndex: page,
    pageSize: pageSize,
    searchKey: searchValue,
    stateProvinceList: [],
    annualExceptionList: [],
    applicationType: applicationType,
    requestorEmailAddress: adminEmail,
    technologyAreaList: [],
    technologyAreaGroupList: [],
    assignedToMe: false,
    technologyAreaScoreList: [],
    technologyAreaStatusList: [],
    technologyAreaStatusReasonList: [],
    flagReason: [],
    cohort: undefined,
    reviewStatusList: [],
    availableCpmList: [],
    sortColumn: sortColumn,
    sortDescending: sortDescending,
  } as any;

  selectedApplicationsFilterValues.forEach(function (item) {
    switch (item.fieldName) {
      case ApplicationConstants.APPLICATION_STATUS:
        requestBody.applicationStatusList.push(item.value);
        break;
      case ApplicationConstants.APPLICATION_COUNTRY_REGION:
        requestBody.countryRegionList.push(item.value);
        break;
      case ApplicationConstants.APPLICATION_STATE_PROVINCE:
        requestBody.stateProvinceList.push(item.value);
        break;
      case ApplicationConstants.GRANT_ANNUAL_EXCEPTION:
        requestBody.annualExceptionList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_GROUP:
        requestBody.technologyAreaGroupList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA:
        requestBody.technologyAreaList.push(item.value);
        break;
      case ApplicationConstants.ASSIGNED_TO:
        requestBody.assignedToMe = item.value === ApplicationConstants.ME;
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_SCORE:
        requestBody.technologyAreaScoreList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_STATUS:
        requestBody.technologyAreaStatusList.push(item.value);
        break;
      case ApplicationConstants.TECHNOLOGY_AREA_STATUS_REASON:
        requestBody.technologyAreaStatusReasonList.push(item.value);
        break;
      case ApplicationConstants.FLAG_REASON:
        requestBody.flagReason.push(item.value);
        break;
      case ApplicationConstants.COHORT:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.cohort = item.value;
        }
        break;
      case ApplicationConstants.REVIEW_STATUS:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.reviewStatusList.push(item.value);
        }
        break;
      case ApplicationConstants.AVAILABLE_CPM_LIST:
        if (item.value !== ApplicationConstants.ANY) {
          requestBody.availableCpmList.push(item.value);
        }
      default:
        return;
    }
  });

  return requestBody;
};
